/**
 * Geo-targeting utilities for the Homepage.
 *
 * These helpers turn a visitor's location (US state) into a "region" so the
 * Homepage can request region-specific Content Fragment variations.
 *
 * Data flow:
 *   x-akamai-edgescape header  ->  state code (e.g. "NY")
 *   state code + regionStatesMapping (from page.model.json)  ->  region (e.g. "east")
 *
 * The `regionStatesMapping` lives in page.model.json and looks like:
 *   { "east": ["NY","PA"], "west": ["CA","WA"], "midwest": ["IL","MI"] }
 *
 * The KEY name `regionStatesMapping` is fixed, but the region keys ("east",
 * "west", ...) are author-configurable and can change at any time, so nothing
 * here hard-codes a region name.
 *
 * There is no hard-coded default region. When a visitor's region cannot be
 * determined, the resolvers return `undefined`; callers then omit the
 * `variation` input entirely and AEM serves the `master` data as the default.
 */

/** Shape of the `regionStatesMapping` object that comes from page.model.json. */
export type RegionStatesMapping = Record<string, string[]>;

/**
 * Name of the base CF variation. The request never asks for it explicitly: the
 * resolved region travels as the `variation` input (DPO-87062 / WEB-207197) and
 * AEM serves `master` when no variation is sent or the requested one does not
 * exist. It is used here only to recognise the `_variation` tag on response
 * entries.
 */
export const MASTER_VARIATION = 'master';

/**
 * Shape of a single entry inside `tabCollectionList.items`: one entry per CF
 * variation returned, each tagged with `_variation` and carrying its own
 * `cards` array. With the `variation` input AEM returns just the one served
 * (the requested region, or `master` as its fallback).
 */
export type TabCollectionItem = {
  _variation?: string;
  cards?: unknown[];
};

/**
 * Pick the correct `cards` array from the `tabCollectionList.items` list.
 *
 * Handles both response shapes:
 *   - single-variation (`variation` input, DPO-87062 / WEB-212859): exactly the
 *     requested variation, or `master` as AEM's fallback;
 *   - multi-variation (legacy `region` filter payloads): one entry per variation.
 * Selection is deterministic:
 *   1. the entry whose `_variation` matches the resolved `region` (case-insensitive), else
 *   2. the entry whose `_variation` is `master`, else
 *   3. the first entry (the single served variation, or an untagged legacy payload).
 *
 * @param items - The `tabCollectionList.items` array from the AEM/UXL response.
 * @param region - The resolved region (e.g. "east"); undefined means master default.
 * @returns The `cards` array for the selected variation, or `[]` when none exist.
 */
export function selectCardsForRegion<T = unknown>(items: TabCollectionItem[] | null | undefined, region?: string): T[] {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }

  const matchesVariation = (item: TabCollectionItem, variation: string) =>
    String(item?._variation ?? '')
      .trim()
      .toLowerCase() === variation.trim().toLowerCase();

  const regionItem = region ? items.find(item => matchesVariation(item, region)) : undefined;
  const masterItem = items.find(item => matchesVariation(item, MASTER_VARIATION));
  const selected = regionItem ?? masterItem ?? items[0];

  return (selected?.cards as T[]) ?? [];
}

/**
 * The field inside the `x-akamai-edgescape` header that holds the visitor's
 * US state/province. Kept as a single constant so it is easy to change if the
 * edge configuration ever uses a different field name.
 */
const EDGESCAPE_STATE_FIELD = 'region_code';

/**
 * Extract the US state code from the raw `x-akamai-edgescape` header value.
 *
 * The header is a comma-separated list of key=value pairs, e.g.
 *   "georegion=193,country_code=US,region_code=NY,city=NEWYORK,..."
 *
 * @param edgescapeHeader - Raw value of the `x-akamai-edgescape` request header.
 * @returns The upper-cased state code (e.g. "NY"), or null if it cannot be found.
 */
export function getStateFromEdgescape(edgescapeHeader: unknown): string | null {
  const match = String(edgescapeHeader || '').match(new RegExp(`${EDGESCAPE_STATE_FIELD}=([A-Za-z]{2})`));
  const state = match?.[1] ? match[1].toUpperCase() : null;
  return state;
}

/**
 * Resolve a US state code to its configured region.
 *
 * Matching is case-insensitive on the state code. If the state is missing or
 * not found in the mapping, `undefined` is returned — there is no default
 * region. Callers then request only the `master` variation, which AEM serves
 * as the default content.
 *
 * @param state - US state code (e.g. "NY"). May be null/undefined.
 * @param regionStatesMapping - Region-to-states map from page.model.json.
 * @returns The matched region key (e.g. "east"), or undefined if unresolved.
 */
export function resolveRegionFromState(
  state: string | null | undefined,
  regionStatesMapping: RegionStatesMapping | null | undefined
): string | undefined {
  if (!state || !regionStatesMapping) {
    return undefined;
  }

  const normalizedState = state.trim().toUpperCase();

  for (const [region, states] of Object.entries(regionStatesMapping)) {
    if (Array.isArray(states) && states.some(s => String(s).trim().toUpperCase() === normalizedState)) {
      return region;
    }
  }

  return undefined;
}

/**
 * Convenience helper that goes straight from the raw `x-akamai-edgescape`
 * header to a region, combining {@link getStateFromEdgescape} and
 * {@link resolveRegionFromState}.
 *
 * @param edgescapeHeader - Raw value of the `x-akamai-edgescape` request header.
 * @param regionStatesMapping - Region-to-states map from page.model.json.
 * @returns The resolved region key, or undefined if unresolved (master default).
 */
export function resolveRegionFromEdgescape(
  edgescapeHeader: unknown,
  regionStatesMapping: RegionStatesMapping | null | undefined
): string | undefined {
  const state = getStateFromEdgescape(edgescapeHeader);
  return resolveRegionFromState(state, regionStatesMapping);
}
